import React from 'react';

interface MarkdownRendererProps {
  content: string;
  darkBackground?: boolean;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, darkBackground = false }) => {
  const textColor = darkBackground ? '#ffffff' : '#262626';
  const codeBackground = darkBackground ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)';
  const codeBorder = darkBackground ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)';
  const linkColor = '#33AA40';

  const parseInline = (text: string): React.ReactNode[] => {
    const nodes: React.ReactNode[] = [];
    let remaining = text;
    let key = 0;

    while (remaining.length > 0) {
      // Bold: **text** or __text__
      const boldMatch = remaining.match(/^(\*\*|__)(.+?)\1/);
      if (boldMatch) {
        nodes.push(
          <strong key={key++} style={{ fontWeight: 600 }}>
            {parseInline(boldMatch[2])}
          </strong>
        );
        remaining = remaining.slice(boldMatch[0].length);
        continue;
      }

      // Italic: *text* or _text_
      const italicMatch = remaining.match(/^(\*|_)(.+?)\1/);
      if (italicMatch) {
        nodes.push(
          <em key={key++}>{parseInline(italicMatch[2])}</em>
        );
        remaining = remaining.slice(italicMatch[0].length);
        continue;
      }

      // Inline code: `code`
      const codeMatch = remaining.match(/^`([^`]+)`/);
      if (codeMatch) {
        nodes.push(
          <code
            key={key++}
            style={{
              backgroundColor: codeBackground,
              border: `1px solid ${codeBorder}`,
              borderRadius: '3px',
              padding: '1px 4px',
              fontSize: '0.875em',
              fontFamily: 'monospace',
            }}
          >
            {codeMatch[1]}
          </code>
        );
        remaining = remaining.slice(codeMatch[0].length);
        continue;
      }

      // Links: [text](url)
      const linkMatch = remaining.match(/^\[([^\]]+)\]\(([^)]+)\)/);
      if (linkMatch) {
        nodes.push(
          <a
            key={key++}
            href={linkMatch[2]}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: linkColor,
              textDecoration: 'underline',
            }}
          >
            {linkMatch[1]}
          </a>
        );
        remaining = remaining.slice(linkMatch[0].length);
        continue;
      }

      // Plain text: consume one character at a time until a special marker
      const nextSpecial = remaining.slice(1).search(/[\*_`\[]/);
      if (nextSpecial === -1) {
        nodes.push(<span key={key++}>{remaining}</span>);
        break;
      } else {
        nodes.push(<span key={key++}>{remaining.slice(0, nextSpecial + 1)}</span>);
        remaining = remaining.slice(nextSpecial + 1);
      }
    }

    return nodes;
  };

  const parseBlock = (text: string): React.ReactNode[] => {
    const lines = text.split('\n');
    const blocks: React.ReactNode[] = [];
    let key = 0;
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      // Code block: ```
      if (line.startsWith('```')) {
        const codeLines: string[] = [];
        i++;
        while (i < lines.length && !lines[i].startsWith('```')) {
          codeLines.push(lines[i]);
          i++;
        }
        i++; // skip closing ```
        blocks.push(
          <pre
            key={key++}
            style={{
              backgroundColor: darkBackground ? 'rgba(0,0,0,0.3)' : '#1F313A',
              color: '#ffffff',
              borderRadius: '6px',
              padding: '12px',
              overflowX: 'auto',
              fontSize: '13px',
              fontFamily: 'monospace',
              margin: '8px 0',
              border: `1px solid ${codeBorder}`,
            }}
          >
            <code>{codeLines.join('\n')}</code>
          </pre>
        );
        continue;
      }

      // Headers: # ## ###
      const headerMatch = line.match(/^(#{1,3})\s+(.+)/);
      if (headerMatch) {
        const level = headerMatch[1].length;
        const fontSize = level === 1 ? '1.25em' : level === 2 ? '1.1em' : '1em';
        blocks.push(
          <div
            key={key++}
            style={{
              fontSize,
              fontWeight: 600,
              margin: '12px 0 6px 0',
              color: textColor,
            }}
          >
            {parseInline(headerMatch[2])}
          </div>
        );
        i++;
        continue;
      }

      // Unordered list: - item or * item
      if (/^[\-\*]\s+/.test(line)) {
        const items: React.ReactNode[] = [];
        while (i < lines.length && /^[\-\*]\s+/.test(lines[i])) {
          const itemText = lines[i].replace(/^[\-\*]\s+/, '');
          items.push(<li key={items.length}>{parseInline(itemText)}</li>);
          i++;
        }
        blocks.push(
          <ul
            key={key++}
            style={{
              margin: '6px 0',
              paddingLeft: '20px',
              listStyleType: 'disc',
            }}
          >
            {items}
          </ul>
        );
        continue;
      }

      // Ordered list: 1. item
      if (/^\d+\.\s+/.test(line)) {
        const items: React.ReactNode[] = [];
        while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
          const itemText = lines[i].replace(/^\d+\.\s+/, '');
          items.push(<li key={items.length}>{parseInline(itemText)}</li>);
          i++;
        }
        blocks.push(
          <ol
            key={key++}
            style={{
              margin: '6px 0',
              paddingLeft: '20px',
              listStyleType: 'decimal',
            }}
          >
            {items}
          </ol>
        );
        continue;
      }

      // Empty line
      if (line.trim() === '') {
        blocks.push(<div key={key++} style={{ height: '8px' }} />);
        i++;
        continue;
      }

      // Regular paragraph
      blocks.push(
        <p key={key++} style={{ margin: '4px 0', lineHeight: '1.5' }}>
          {parseInline(line)}
        </p>
      );
      i++;
    }

    return blocks;
  };

  const containerStyle: React.CSSProperties = {
    color: textColor,
    fontSize: '14px',
    lineHeight: '1.5',
    overflowWrap: 'break-word',
  };

  return <div style={containerStyle}>{parseBlock(content)}</div>;
};

export default MarkdownRenderer;
